import type { ReactNode } from 'react';
import clsx from 'clsx';
import './Stamp.css';

export type Tone = 'ok' | 'warn' | 'alert' | 'info' | 'accent' | 'neutral';

interface StampProps {
  children: ReactNode;
  tone?: Tone;
  /** `solid` is reserved for terminal states — paid, delivered, cancelled. */
  weight?: 'outline' | 'solid';
  size?: 'sm' | 'md';
  /** Optional leading dot for a second axis of meaning (e.g. unread). */
  dot?: boolean;
  title?: string;
  className?: string;
}

/**
 * The status token. Not a pill.
 *
 * Every other CRM renders state as a rounded, pastel-filled chip.
 * Prody renders it as a rubber ink stamp: square-cornered, hairline
 * bordered, condensed uppercase, tracked out. It reads as something
 * applied to a document rather than something decorating a row — which
 * is what a status actually is in this business, where the paper trail
 * is the product.
 *
 * Colour on a stamp carries workflow state. Colour on the row band
 * carries what the goods are. Keeping those two on different surfaces
 * is what stops the tables turning into a rainbow.
 */
export function Stamp({
  children,
  tone = 'neutral',
  weight = 'outline',
  size = 'md',
  dot = false,
  title,
  className,
}: StampProps) {
  return (
    <span
      className={clsx('pd-stamp', className)}
      data-tone={tone}
      data-weight={weight}
      data-size={size}
      title={title}
    >
      {dot ? <span className="pd-stamp__dot" aria-hidden="true" /> : null}
      {children}
    </span>
  );
}

/**
 * The leading edge of a row or card, coloured by handling class.
 * Rendered as a real element rather than a border so it can carry a
 * tooltip — a driver needs to know *why* the row is blue.
 */
export function Band({
  handlingClass,
  label,
  className,
}: {
  handlingClass: string;
  label?: string;
  className?: string;
}) {
  return (
    <span
      className={clsx('pd-band', className)}
      data-hc={handlingClass}
      title={label}
      aria-hidden={label ? undefined : 'true'}
      role={label ? 'img' : undefined}
      aria-label={label}
    />
  );
}

/** Short uppercase entity code: SKU, party code, route name. */
export function Code({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={clsx('pd-code', className)}>{children}</span>;
}
